// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import ProductListProductInformation from "@insite/content-library/Widgets/ProductList/ProductListProductInformation";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductListGridContainer, {
    ProductListGridContainerStyle,
} from "@steiner/App/Components/Product/ProductListGridContainer";
import ProductPriceAndActions, {
    ProductPriceAndActionsStyle,
} from "@steiner/App/Components/Product/ProductPriceAndActions";
import { ProductListProductInformationStyles } from "@steiner/Overrides/Widgets/ProductList/ProductListProductInformation";
import React, { FC } from "react";
import { css } from "styled-components";

type Props = ProductCardSelections & HasProduct;

export interface ProductListProductGridCardStyles {
    wrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    sponsoredText?: TypographyPresentationProps;
    // XNG-Changes
    productListGridContainerStyles?: ProductListGridContainerStyle;
    productListImageStyles?: ProductListProductImageStyles;
    productInformationStyles?: ProductListProductInformationStyles;
    productPriceAndActionsStyles?: ProductPriceAndActionsStyle;
}

export const productGridCardStyles: ProductListProductGridCardStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            overflow: hidden;
            padding-bottom: 10px;
        `,
    },
    imageWrapper: {
        css: css`
            margin: auto;
            padding: 10px 50px;
        `,
    },
    sponsoredText: {
        css: css`
            font-size: 0.9em;
            opacity: 0.5;
            position: absolute;
            top: 12px;
        `,
    },
};

const styles = productGridCardStyles;

const ProductListProductGridCard: FC<Props> = ({
    product,
    showImage,
    showCompare,
    showAttributes,
    showAvailability,
    showBrand,
    showPartNumbers,
    showTitle,
    showAddToList,
    showPrice,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
}) => {
    return (
        <ProductListGridContainer
            extendedStyles={styles.productListGridContainerStyles}
            id={`product_${product.id}`}
            data-test-selector="productContainer"
        >
            <ProductListGridContainer.Image>
                <ProductListProductImage
                    showImage={showImage}
                    showCompare={false}
                    showImageBadge={showImageBadges}
                    showTextBadge={showTextBadges}
                    extendedStyles={styles.productListImageStyles}
                />
            </ProductListGridContainer.Image>
            <ProductListGridContainer.Info>
                <ProductListProductInformation
                    showAttributes={showAttributes}
                    showAvailability={false}
                    showBrand={showBrand}
                    showPartNumbers={showPartNumbers}
                    showTitle={showTitle}
                    showImageBadges={showImageBadges}
                    maxImageBadges={maximumNumberOfImageBadges}
                    showTextBadges={false}
                    maxTextBadges={maximumNumberOfTextBadges}
                    {...{ extendedStyles: styles.productInformationStyles }}
                />
            </ProductListGridContainer.Info>
            <ProductListGridContainer.AddToCartActions>
                <ProductPriceAndActions
                    showAvailability={showAvailability}
                    showCompare={showCompare}
                    showAddToList={showAddToList}
                    showPrice={showPrice}
                    showAddToCartActions={true}
                    view="Grid"
                    extendedStyles={styles.productPriceAndActionsStyles}
                    containerClassName="pricing-info-container"
                />
            </ProductListGridContainer.AddToCartActions>
        </ProductListGridContainer>
    );
};

export default withProduct(ProductListProductGridCard);
